import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  People as UsersIcon,
  Groups as FamiliesIcon,
  Description as TicketsIcon,
  TrendingUp as GrowthIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { format, subDays, startOfDay } from 'date-fns';

const SystemAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFamilies: 0,
    activeUsersToday: 0,
    newUsersThisWeek: 0,
    totalTickets: 0,
    openTickets: 0,
    totalPhotos: 0,
    totalEvents: 0,
    totalAnnouncements: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [topFamilies, setTopFamilies] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Count unique family codes
      const familyCodes = new Set(users.filter(u => u.familyCode).map(u => u.familyCode));

      // Get recent users (last 7 days)
      const weekAgo = subDays(new Date(), 7);
      const recentUsersList = users
        .filter(u => u.createdAt?.toDate && u.createdAt.toDate() > weekAgo)
        .sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate())
        .slice(0, 10);

      // Get users active today (based on lastLogin or updatedAt)
      const today = startOfDay(new Date());
      const activeToday = users.filter(u => {
        const lastActive = u.lastLogin?.toDate() || u.updatedAt?.toDate();
        return lastActive && lastActive >= today;
      }).length;

      // Get ticket stats
      let ticketStats = { total: 0, open: 0 };
      try {
        const ticketsSnapshot = await getDocs(collection(db, 'supportTickets'));
        const tickets = ticketsSnapshot.docs.map(doc => doc.data());
        ticketStats.total = tickets.length;
        ticketStats.open = tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length;
      } catch (e) {
        logger.log('No tickets collection yet');
      }

      // Calculate family sizes
      const familySizes = {};
      users.forEach(u => {
        if (u.familyCode) {
          familySizes[u.familyCode] = (familySizes[u.familyCode] || 0) + 1;
        }
      });

      const topFamiliesList = Object.entries(familySizes)
        .map(([code, count]) => ({ familyCode: code, memberCount: count }))
        .sort((a, b) => b.memberCount - a.memberCount)
        .slice(0, 5);

      setStats({
        totalUsers: users.length,
        totalFamilies: familyCodes.size,
        activeUsersToday: activeToday,
        newUsersThisWeek: recentUsersList.length,
        totalTickets: ticketStats.total,
        openTickets: ticketStats.open,
        totalPhotos: 0, // Would need to query photos collection
        totalEvents: 0, // Would need to query events collection
        totalAnnouncements: 0 // Would need to query announcements collection
      });

      setRecentUsers(recentUsersList);
      setTopFamilies(topFamiliesList);

    } catch (err) {
      logger.error('Error loading analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h3" fontWeight={700} color={`${color}.main`}>
              {value}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Icon sx={{ fontSize: 48, color: `${color}.main`, opacity: 0.3 }} />
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Analytics Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Overview of platform usage and growth metrics
      </Typography>

      {/* Main Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={UsersIcon}
            title="Total Users"
            value={stats.totalUsers}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={FamiliesIcon}
            title="Families"
            value={stats.totalFamilies}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={GrowthIcon}
            title="Active Today"
            value={stats.activeUsersToday}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={TicketsIcon}
            title="Open Tickets"
            value={stats.openTickets}
            subtitle={`${stats.totalTickets} total`}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Users */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Signups (Last 7 Days)
            </Typography>
            {recentUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No new users in the last 7 days
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Family</TableCell>
                      <TableCell>Joined</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentUsers.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {user.displayName || `${user.firstName} ${user.lastName}` || user.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {user.familyCode ? (
                            <Chip label={user.familyCode} size="small" />
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {user.createdAt?.toDate ? format(user.createdAt.toDate(), 'MMM d') : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Top Families */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Largest Families
            </Typography>
            {topFamilies.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No family data available
              </Typography>
            ) : (
              <Box>
                {topFamilies.map((family, index) => (
                  <Box key={family.familyCode} sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        {family.familyCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {family.memberCount} members
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((family.memberCount / (topFamilies[0]?.memberCount || 1)) * 100, 100)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.800',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: index === 0 ? 'success.main' : index === 1 ? 'info.main' : 'grey.500'
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Growth Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Platform Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" color="primary.main">{stats.newUsersThisWeek}</Typography>
                  <Typography variant="body2" color="text.secondary">New Users (7 days)</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" color="success.main">
                    {stats.totalFamilies > 0 ? (stats.totalUsers / stats.totalFamilies).toFixed(1) : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Avg Members/Family</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" color="info.main">{stats.totalTickets}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Support Tickets</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" color="warning.main">
                    {stats.totalUsers > 0 ? ((stats.activeUsersToday / stats.totalUsers) * 100).toFixed(0) : 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Daily Active Rate</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemAnalytics;
